// ── Voice Chat Manager ──
// Manages WebRTC audio tracks for push-to-talk team voice chat.
// Uses the existing PeerConnection from connection.ts to add/remove audio tracks.
// The server acts as an SFU — it receives each player's audio and relays
// it to teammates only.

/** Voice activity detection threshold (0-1). Adjustable from settings. */
export let VAD_THRESHOLD = 0.02;

/** Set the VAD threshold (0-1). */
export function setVadThreshold(t: number): void {
  VAD_THRESHOLD = Math.max(0, Math.min(1, t));
}

/**
 * Read the current VAD threshold (0-1).
 *
 * F-AV-009: provides a getter so a future settings slider can seed its
 * initial value without reading the module-level binding directly. The
 * running VAD interval re-reads the binding every 50ms tick (closures
 * over `export let` see updates), so setVadThreshold takes effect live.
 */
export function getVadThreshold(): number {
  return VAD_THRESHOLD;
}

export type MicPermissionState = 'granted' | 'denied' | 'prompt' | 'unknown';
export type VoiceState = 'inactive' | 'connecting' | 'active' | 'error';

/**
 * A-M-006: PTT transmission lifecycle.
 *   idle    — PTT not pressed (or mic is muted / preflight denied)
 *   warming — PTT pressed; preflight (mic permission + AudioContext resume)
 *             still in flight; the WebRTC track is NOT yet enabled
 *   live    — preflight resolved 'ok' and startTransmitting() flipped the
 *             track enabled — VAD events surface to listeners
 *
 * The VAD analyser runs continuously off the local MediaStream so its
 * amplitude readings reflect mic input even before the track is enabled.
 * Without this state machine the HUD flashes LIVE during the preflight
 * gap (~50-300 ms on first PTT) because VAD reports `speaking=true` on
 * any ambient noise.
 */
export type TransmitState = 'idle' | 'warming' | 'live';

export interface VoicePeer {
  playerId: number;
  speaking: boolean;
  name?: string;
}

/**
 * VoiceChatManager handles local microphone capture and remote audio playback.
 * It piggybacks on the existing WebRTC PeerConnection used for game data.
 */
export class VoiceChatManager {
  private localStream: MediaStream | null = null;
  private localAudioTrack: MediaStreamTrack | null = null;
  private sender: RTCRtpSender | null = null;
  private pc: RTCPeerConnection | null = null;

  private peers = new Map<number, VoicePeer>();
  private peerAudio = new Map<number, HTMLAudioElement>();
  private _state: VoiceState = 'inactive';
  private _muted = false;
  private _voiceVolume = 1.0;
  private _transmitting = false;
  /** A-M-006: PTT transmit lifecycle gate for VAD UI events. */
  private _transmitState: TransmitState = 'idle';

  private listeners: Array<() => void> = [];
  private permissionListeners: Array<(state: MicPermissionState) => void> = [];
  private vadListeners: Array<(speaking: boolean) => void> = [];
  /** Single shared PermissionStatus watcher — avoids each listener
   * overwriting `status.onchange` from a fresh query. */
  private permissionStatus: PermissionStatus | null = null;
  private permissionStatusPending: Promise<void> | null = null;
  /** F-AV-013: stored handler ref so `dispose()` can detach it cleanly. */
  private permissionStatusHandler: (() => void) | null = null;
  /** F-AV-008: idempotency flag — match AnnouncerSystem/MusicSystem contract. */
  private disposed = false;
  private pendingAutoplay = new Set<HTMLAudioElement>();
  private autoplayResumeHandler: (() => void) | null = null;
  private renegotiateHandler: (() => void) | null = null;
  private analyserNode: AnalyserNode | null = null;
  private audioContext: AudioContext | null = null;
  private vadInterval: ReturnType<typeof setInterval> | null = null;
  private _localSpeaking = false;

  /** Get the current voice state. */
  getState(): VoiceState {
    return this._state;
  }

  /** Whether the local user is muted. */
  isMuted(): boolean {
    return this._muted;
  }

  /** Whether the local user is currently transmitting (PTT active + mic acquired). */
  isTransmitting(): boolean {
    return this._transmitting;
  }

  /**
   * A-M-006: read the current PTT transmit state. Callers (UI) use this to
   * gate LIVE indicators on `state === 'live'` so the LIVE label does not
   * flash during the preflight async gap.
   */
  getTransmitState(): TransmitState {
    return this._transmitState;
  }

  /**
   * A-M-006: explicitly set the transmit lifecycle state. The PTT keydown
   * handler calls this synchronously with 'warming' before awaiting the
   * preflight; on resolved 'ok' it then calls `startTransmitting()` which
   * flips state to 'live'. On preflight denial or release, `stopTransmitting()`
   * brings state back to 'idle'.
   *
   * Suppresses any in-flight VAD `speaking=true` event when transitioning
   * out of 'live', so the UI cannot latch a stale speaking flag after the
   * mic stops.
   */
  setTransmitState(state: TransmitState): void {
    if (this._transmitState === state) return;
    this._transmitState = state;
    if (state !== 'live' && this._localSpeaking) {
      this._localSpeaking = false;
      this.notifyVAD(false);
    }
  }

  /** Get all currently known voice peers. */
  getPeers(): ReadonlyMap<number, VoicePeer> {
    return this.peers;
  }

  /** Get current voice volume (0-1). */
  getVoiceVolume(): number {
    return this._voiceVolume;
  }

  /**
   * Register a callback that is invoked whenever the local PeerConnection has
   * been mutated (e.g. addTrack) and needs a fresh SDP offer sent to the SFU.
   * The connection layer owns the signalling channel, so it must handle the
   * actual offer/answer exchange.
   */
  onNegotiationNeeded(fn: () => void): void {
    this.renegotiateHandler = fn;
  }

  private requestRenegotiation(): void {
    if (this.renegotiateHandler) {
      try { this.renegotiateHandler(); } catch { /* swallow */ }
    }
  }

  /**
   * Attempt to play a remote audio element. If the browser rejects playback
   * because of the autoplay policy, retry once a user gesture occurs.
   */
  private tryPlayAudio(audio: HTMLAudioElement): void {
    const p = audio.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        this.pendingAutoplay.add(audio);
        this.installAutoplayResumer();
      });
    }
  }

  private installAutoplayResumer(): void {
    if (this.autoplayResumeHandler) return;
    if (typeof document === 'undefined') return;
    const handler = () => {
      for (const audio of this.pendingAutoplay) {
        audio.play().catch(() => { /* still blocked — leave for next gesture */ });
      }
      this.pendingAutoplay.clear();
      if (this.autoplayResumeHandler) {
        document.removeEventListener('pointerdown', this.autoplayResumeHandler);
        document.removeEventListener('keydown', this.autoplayResumeHandler);
        document.removeEventListener('touchstart', this.autoplayResumeHandler);
        this.autoplayResumeHandler = null;
      }
    };
    this.autoplayResumeHandler = handler;
    document.addEventListener('pointerdown', handler, { once: false });
    document.addEventListener('keydown', handler, { once: false });
    document.addEventListener('touchstart', handler, { once: false });
  }

  /** Attach to an existing PeerConnection (from connection.ts WebRTC upgrade). */
  attachPeerConnection(pc: RTCPeerConnection): void {
    this.pc = pc;

    // Listen for incoming audio tracks from server (other players' voice)
    pc.ontrack = (ev: RTCTrackEvent) => {
      if (ev.track.kind !== 'audio') return;

      // Extract player ID from stream ID — server sets stream ID to "voice-{playerID}"
      const streamId = ev.streams[0]?.id ?? '';
      const match = streamId.match(/^voice-(\d+)$/);
      if (!match) return;
      const playerId = parseInt(match[1], 10);

      const stream = ev.streams[0];
      const audio = new Audio();
      audio.srcObject = stream;
      audio.volume = this._voiceVolume;
      this.tryPlayAudio(audio);

      this.peerAudio.set(playerId, audio);
      if (!this.peers.has(playerId)) {
        this.peers.set(playerId, { playerId, speaking: true });
      }

      // Detect when track ends (player stops PTT or disconnects)
      ev.track.onended = () => {
        this.removePeer(playerId);
      };

      this.notify();
    };
  }

  /** Acquire microphone. Call once during game init. */
  async acquireMicrophone(): Promise<boolean> {
    try {
      this._state = 'connecting';
      this.notify();

      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });

      this.localAudioTrack = this.localStream.getAudioTracks()[0];
      // Start muted — will unmute on PTT press
      this.localAudioTrack.enabled = false;

      // Add track to PeerConnection if available
      if (this.pc) {
        this.sender = this.pc.addTrack(this.localAudioTrack, this.localStream);
        // addTrack mutates the local SDP — the server SFU needs a renegotiated offer
        // to learn about the new audio track.
        this.requestRenegotiation();
      }

      this._state = 'active';
      this.notify();
      this.startVAD();
      return true;
    } catch {
      this._state = 'error';
      this.notify();
      return false;
    }
  }

  /** Start transmitting (push-to-talk pressed). */
  startTransmitting(): void {
    if (!this.localAudioTrack || this._muted) return;
    this.localAudioTrack.enabled = true;
    this._transmitting = true;
    // A-M-006: mic is actually live now — VAD events may surface to UI.
    this._transmitState = 'live';
    this.notify();
  }

  /** F-MOB-M4: pre-flight check before PTT — verifies mic permission isn't
   *  denied and unlocks the AudioContext if it's suspended. Returns:
   *    'ok'      — safe to call startTransmitting()
   *    'denied'  — mic permission denied; UI should show a banner
   *    'no-track' — mic was never acquired (call acquireMicrophone first)
   *  Does NOT call startTransmitting itself — the caller decides whether to
   *  proceed. Idempotent and safe to call from any context. */
  async preflightPTT(): Promise<'ok' | 'denied' | 'no-track'> {
    // Check mic permission first — Permissions API returns 'denied' when the
    // user blocked the prompt. We don't probe by calling getUserMedia here
    // because that would re-prompt on browsers that have already granted.
    try {
      if (typeof navigator !== 'undefined' && navigator.permissions) {
        const status = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        if (status.state === 'denied') {
          this.notifyPermission('denied');
          return 'denied';
        }
      }
    } catch {
      // Permissions API unsupported — fall through to track/AudioContext checks.
    }

    // Resume AudioContext if it's suspended. Browsers (especially Safari iOS)
    // start the context suspended until a user gesture; PTT is a user gesture
    // so this is the right place to flip it.
    if (this.audioContext && this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
      } catch {
        // best-effort — leave it suspended; downstream call may still succeed.
      }
    }

    if (!this.localAudioTrack) return 'no-track';
    return 'ok';
  }

  /** Stop transmitting (push-to-talk released). */
  stopTransmitting(): void {
    // A-M-006: always drop transmit state on release — including the
    // release-before-acquire path (voice-input rebind/dispose with no mic
    // ever acquired). Setting state back to 'idle' must happen regardless
    // so a downstream LIVE indicator clears.
    const wasLive = this._transmitState === 'live';
    this._transmitState = 'idle';
    if (this._localSpeaking) {
      this._localSpeaking = false;
      if (wasLive) this.notifyVAD(false);
    }
    if (!this.localAudioTrack) return;
    this.localAudioTrack.enabled = false;
    this._transmitting = false;
    this.notify();
  }

  /** Toggle mute state. When muted, PTT has no effect. */
  toggleMute(): void {
    this._muted = !this._muted;
    if (this._muted && this.localAudioTrack) {
      this.localAudioTrack.enabled = false;
      this._transmitting = false;
    }
    this.notify();
  }

  /** Set mute state explicitly. */
  setMuted(muted: boolean): void {
    if (this._muted === muted) return;
    this._muted = muted;
    if (this._muted && this.localAudioTrack) {
      this.localAudioTrack.enabled = false;
      this._transmitting = false;
    }
    this.notify();
  }

  /** Set voice volume for remote players (0-1). */
  setVoiceVolume(vol: number): void {
    this._voiceVolume = Math.max(0, Math.min(1, vol));
    for (const audio of this.peerAudio.values()) {
      audio.volume = this._voiceVolume;
    }
    this.notify();
  }

  /**
   * Register a peer's audio element for volume control.
   *
   * Wire-Not-Delete (F-AV-011): production peer audio currently arrives
   * via `pc.ontrack` (line ~137), which already calls `peerAudio.set(...)`
   * inline. This entry point exists for non-WebRTC playback paths the
   * roadmap reserves for replay / demo / GOTV systems where a piped
   * audio element (or `<audio src=blob:...>`) needs to be folded into
   * the live voice mixer's volume control. It is also exercised by
   * `voice.test.ts` setVoiceVolume tests (3 call sites). Keep this hook
   * available — deleting it breaks both the unit suite and the documented
   * future replay-audio integration point.
   */
  addPeerAudio(playerId: number, audio: HTMLAudioElement): void {
    this.peerAudio.set(playerId, audio);
    audio.volume = this._voiceVolume;
    if (!this.peers.has(playerId)) {
      this.peers.set(playerId, { playerId, speaking: false });
    }
  }

  /**
   * Handle voice events from the server (speaking state of other players).
   * Server sends these as JSON messages: { type: "voice_speaking", data: { playerId, speaking } }
   */
  handleVoiceEvent(type: string, data: Record<string, unknown>): void {
    if (type !== 'voice_speaking') return;

    const playerId = data.playerId as number;
    const speaking = data.speaking as boolean;

    const existing = this.peers.get(playerId);
    if (existing) {
      existing.speaking = speaking;
    } else {
      this.peers.set(playerId, { playerId, speaking, name: data.name as string | undefined });
    }
    this.notify();
  }

  /** Register a listener for voice state changes. */
  onChange(fn: () => void): void {
    this.listeners.push(fn);
  }

  /** Remove a voice state change listener. */
  offChange(fn: () => void): void {
    this.listeners = this.listeners.filter((f) => f !== fn);
  }

  /** Remove a disconnected peer. */
  removePeer(playerId: number): void {
    const audio = this.peerAudio.get(playerId);
    if (audio) {
      audio.pause();
      audio.srcObject = null;
      this.peerAudio.delete(playerId);
    }
    this.peers.delete(playerId);
    this.notify();
  }

  /**
   * Request microphone permission from the browser.
   * Shows the permission prompt and returns true if granted, false otherwise.
   */
  async requestMicPermission(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      // Stop the tracks immediately — we just needed the permission
      for (const track of stream.getTracks()) track.stop();
      this.notifyPermission('granted');
      return true;
    } catch {
      this.notifyPermission('denied');
      return false;
    }
  }

  /**
   * Check current microphone permission state without triggering a prompt.
   */
  async getMicPermissionState(): Promise<MicPermissionState> {
    try {
      if (!navigator.permissions) return 'unknown';
      const status = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      return status.state as MicPermissionState;
    } catch {
      return 'unknown';
    }
  }

  /**
   * Register a callback for microphone permission state changes.
   * The callback fires when the user grants or denies mic access.
   */
  onPermissionChange(callback: (state: MicPermissionState) => void): void {
    this.permissionListeners.push(callback);
    this.ensurePermissionWatcher();
  }

  /**
   * Query the Permissions API once and use `addEventListener('change')` so
   * that every registered listener sees live permission updates. Using
   * `status.onchange` from multiple listener registrations would overwrite
   * itself and leave only the last registration receiving events.
   */
  private ensurePermissionWatcher(): void {
    if (this.permissionStatus || this.permissionStatusPending) return;
    if (typeof navigator === 'undefined' || !navigator.permissions) return;
    this.permissionStatusPending = navigator.permissions
      .query({ name: 'microphone' as PermissionName })
      .then((status) => {
        this.permissionStatus = status;
        // F-AV-013: keep a ref to the change handler so `dispose()` can
        // call removeEventListener and let the closure (which captures
        // `this`) be GC'd. Without this, every test-instantiated
        // VoiceChatManager leaks one PermissionStatus listener.
        const handler = () => {
          this.notifyPermission(status.state as MicPermissionState);
        };
        this.permissionStatusHandler = handler;
        status.addEventListener('change', handler);
      })
      .catch(() => { /* ignore */ })
      .finally(() => {
        this.permissionStatusPending = null;
      });
  }

  /**
   * Remove a permission change listener.
   */
  offPermissionChange(callback: (state: MicPermissionState) => void): void {
    this.permissionListeners = this.permissionListeners.filter((f) => f !== callback);
  }

  /**
   * Register a callback for voice activity detection events.
   * Fires with true when the user starts speaking, false when they stop.
   */
  onVoiceActivity(callback: (speaking: boolean) => void): void {
    this.vadListeners.push(callback);
  }

  /**
   * Remove a voice activity listener.
   */
  offVoiceActivity(callback: (speaking: boolean) => void): void {
    this.vadListeners = this.vadListeners.filter((f) => f !== callback);
  }

  /** Whether the local user is currently speaking (per VAD). */
  isLocalSpeaking(): boolean {
    return this._localSpeaking;
  }

  /** Start voice activity detection on the local mic stream. */
  private startVAD(): void {
    if (!this.localStream || this.vadInterval) return;

    try {
      // Reuse existing AudioContext if one is already open; otherwise create new
      if (!this.audioContext) {
        this.audioContext = new AudioContext();
      }
      // Resume context — Chrome autoplay policy starts it suspended
      this.audioContext.resume().catch(() => { /* context may be closed */ });

      const source = this.audioContext.createMediaStreamSource(this.localStream);
      this.analyserNode = this.audioContext.createAnalyser();
      this.analyserNode.fftSize = 256;
      source.connect(this.analyserNode);

      const dataArray = new Uint8Array(this.analyserNode.frequencyBinCount);

      this.vadInterval = setInterval(() => {
        if (!this.analyserNode) return;
        // F-AV-007: skip the FFT read entirely while the user is muted.
        // Background hum still triggers `_localSpeaking` flips on the
        // muted track which (a) burns CPU on a 50 ms tick and (b) emits
        // VAD events that listeners must filter out. When muted, force
        // _localSpeaking back to false and short-circuit so the analyser
        // does no work until unmute.
        if (this._muted) {
          if (this._localSpeaking) {
            this._localSpeaking = false;
            this.notifyVAD(false);
          }
          return;
        }
        this.analyserNode.getByteFrequencyData(dataArray);

        // Compute average amplitude (0-1 range)
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
        const average = sum / dataArray.length / 255;

        this._onVadAmplitude(average > VAD_THRESHOLD);
      }, 50); // Check every 50ms
    } catch {
      // AudioContext not available
    }
  }

  /**
   * A-M-006: amplitude → speaking flip with transmit-state gating. Both the
   * analyser interval and tests funnel through this method so the warming/
   * live decision lives in one place.
   *
   * When `_transmitState !== 'live'` (idle or warming), VAD events are
   * suppressed entirely: the local flag is forced false and listeners
   * receive a `speaking=false` event if a stale `true` had latched. This is
   * what stops the HUD's LIVE indicator flashing during the preflight gap.
   */
  private _onVadAmplitude(above: boolean): void {
    if (this._transmitState !== 'live') {
      if (this._localSpeaking) {
        this._localSpeaking = false;
        this.notifyVAD(false);
      }
      return;
    }
    if (above !== this._localSpeaking) {
      this._localSpeaking = above;
      this.notifyVAD(above);
    }
  }

  /** Stop voice activity detection. */
  private stopVAD(): void {
    if (this.vadInterval) {
      clearInterval(this.vadInterval);
      this.vadInterval = null;
    }
    this.analyserNode = null;
    // Close AudioContext if open — capture ref before nulling to avoid leaks
    const ctx = this.audioContext;
    this.audioContext = null;
    if (ctx) {
      if (ctx.state !== 'closed') {
        ctx.close().catch(() => { /* already closed or unavailable */ });
      }
    }
    this._localSpeaking = false;
  }

  private notifyPermission(state: MicPermissionState): void {
    for (const fn of this.permissionListeners) fn(state);
  }

  private notifyVAD(speaking: boolean): void {
    for (const fn of this.vadListeners) fn(speaking);
  }

  /** Clean up all resources. */
  dispose(): void {
    // F-AV-008: idempotency guard — match AnnouncerSystem/MusicSystem so
    // a second dispose() is a cheap no-op rather than re-firing notify
    // and double-closing the AudioContext.
    if (this.disposed) return;
    this.disposed = true;

    // Stop local tracks
    if (this.localStream) {
      for (const track of this.localStream.getTracks()) {
        track.stop();
      }
      this.localStream = null;
      this.localAudioTrack = null;
    }

    // Remove sender from PC
    if (this.sender && this.pc) {
      try { this.pc.removeTrack(this.sender); } catch { /* ignore */ }
      this.sender = null;
    }

    // Clean up peers
    for (const audio of this.peerAudio.values()) {
      try { audio.pause(); } catch { /* ignore */ }
      try { audio.srcObject = null; } catch { /* ignore */ }
    }
    this.peerAudio.clear();
    this.peers.clear();
    this.pendingAutoplay.clear();
    if (this.autoplayResumeHandler && typeof document !== 'undefined') {
      document.removeEventListener('pointerdown', this.autoplayResumeHandler);
      document.removeEventListener('keydown', this.autoplayResumeHandler);
      document.removeEventListener('touchstart', this.autoplayResumeHandler);
      this.autoplayResumeHandler = null;
    }

    // F-AV-013: detach the PermissionStatus 'change' listener so its
    // closure (which captures `this`) can be GC'd. Without this, every
    // test-instantiated VoiceChatManager leaks one listener + one ref
    // to `this`.
    if (this.permissionStatus && this.permissionStatusHandler) {
      try {
        this.permissionStatus.removeEventListener('change', this.permissionStatusHandler);
      } catch { /* ignore */ }
    }
    this.permissionStatus = null;
    this.permissionStatusHandler = null;

    // Stop VAD before clearing listener arrays so any final VAD event can fire.
    this.stopVAD();

    // F-AV-007: VAD wasn't started but `preflightPTT()` may have created
    // an orphan AudioContext via `audioContext.resume()` — close it here
    // so we don't leak contexts on browsers with strict caps (Safari ~6).
    // stopVAD() already handles the started-VAD path; this only fires when
    // the user pre-flighted but never PTT'd before disposal.
    if (this.audioContext) {
      const ctx = this.audioContext;
      this.audioContext = null;
      if (ctx.state !== 'closed') {
        try { ctx.close().catch(() => { /* already closed */ }); } catch { /* ignore */ }
      }
    }

    // Transition to inactive and fire a final notify so UI (voice indicators,
    // etc.) can react to the state change before we drop their subscriptions.
    this._state = 'inactive';
    this._transmitting = false;
    this.pc = null;
    this.renegotiateHandler = null;
    this.notify();

    this.listeners = [];
    this.permissionListeners = [];
    this.vadListeners = [];
  }

  private notify(): void {
    for (const fn of this.listeners) fn();
  }
}

/** Singleton voice chat manager. */
export const voiceChat = new VoiceChatManager();
